import CardMenu from "components/card/CardMenu";
import Card from "components/card";
import { MdCheckCircle, MdAccessTime, MdOutlineSchool, MdStar, MdPlayCircle, MdSchedule, MdBookmark, MdOpenInNew, MdWarning, MdRefresh } from "react-icons/md";
import { useNavigate } from "react-router-dom";

const CourseTable = (props) => {
  const { columnsData, tableData, remainingSubscriptions } = props;
  const navigate = useNavigate();

  // Helper function to get subscription type styling
  const getSubscriptionTypeStyle = (type) => {
    switch (type?.toLowerCase()) {
      case 'premium':
        return {
          bgColor: 'bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20',
          textColor: 'text-purple-700 dark:text-purple-300',
          borderColor: 'border-purple-200 dark:border-purple-700',
          icon: <MdStar className="text-purple-500" />,
          badge: 'bg-purple-500',
          accent: 'from-purple-500 to-purple-600'
        };
      case 'standard':
        return {
          bgColor: 'bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20',
          textColor: 'text-blue-700 dark:text-blue-300',
          borderColor: 'border-blue-200 dark:border-blue-700',
          icon: <MdOutlineSchool className="text-blue-500" />,
          badge: 'bg-blue-500',
          accent: 'from-blue-500 to-blue-600'
        };
      default:
        return {
          bgColor: 'bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900/20 dark:to-gray-800/20',
          textColor: 'text-gray-700 dark:text-gray-300',
          borderColor: 'border-gray-200 dark:border-gray-700',
          icon: <MdOutlineSchool className="text-gray-500" />,
          badge: 'bg-gray-500',
          accent: 'from-gray-500 to-gray-600'
        };
    }
  };

  // Helper function to check if subscription is expired
  const isSubscriptionExpired = (expirationDate) => {
    if (!expirationDate) return false;
    return new Date() > new Date(expirationDate);
  };

  // Helper function to get status styling
  const getStatusStyle = (expirationDate) => {
    const isExpired = isSubscriptionExpired(expirationDate);
    
    if (!isExpired) {
      return {
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        textColor: 'text-green-700 dark:text-green-400',
        borderColor: 'border-green-200 dark:border-green-700',
        icon: <MdCheckCircle className="text-green-500" />,
        text: 'Active'
      };
    } else {
      return {
        bgColor: 'bg-orange-100 dark:bg-orange-900/30',
        textColor: 'text-orange-700 dark:text-orange-400',
        borderColor: 'border-orange-200 dark:border-orange-700',
        icon: <MdWarning className="text-orange-500" />,
        text: 'Expired'
      };
    }
  };

  // Create a map of course subscriptions for quick lookup
  const subscriptionMap = remainingSubscriptions?.reduce((acc, sub) => {
    acc[sub.courseId] = sub;
    return acc;
  }, {}) || {};

  const handleAccessCourse = () => {
    // Navigate to the course page
    navigate('/admin/course');
  };

  const handleViewDetails = () => {
    // Navigate to the courses page
    navigate('/admin/courses');
  };

  const handleRenewSubscription = () => {
    // Navigate to the courses page for renewal
    navigate('/admin/courses');
  };

  return (
    <Card extra={"px-6 w-full h-full pt-[28px] pb-4 flex flex-col"}>
      <div className="mb-6 mt-3 flex items-center justify-between">
        <div className="text-lg font-bold text-navy-700 dark:text-white flex items-center gap-2">
          <MdOutlineSchool className="text-brand-500" />
          My Courses
        </div>
        <CardMenu />
      </div>

      <div className="flex flex-grow flex-col overflow-x-scroll xl:overflow-hidden min-h-[200px]">
        {tableData.length === 0 ? (
          <div className="flex flex-col items-center justify-center flex-grow min-h-[200px] text-center">
            <div className="w-16 h-16 bg-gray-100 dark:bg-navy-800 rounded-full flex items-center justify-center mb-4">
              <MdOutlineSchool className="text-2xl text-gray-400" />
            </div>
            <p className="text-gray-500 dark:text-gray-400 mb-2 font-medium">
              No courses enrolled yet
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Start your learning journey by enrolling in a course
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {tableData.map((course, index) => {
              const subscription = subscriptionMap[course.courseId];
              const subscriptionStyle = getSubscriptionTypeStyle(course.subscriptionType);
              const isExpired = isSubscriptionExpired(subscription?.expirationDate);
              const statusStyle = getStatusStyle(subscription?.expirationDate);

              return (
                <div
                  key={index}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-lg hover:scale-[1.02] ${subscriptionStyle.bgColor} ${subscriptionStyle.borderColor}`}
                >
                  {/* Course Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-base font-bold text-navy-700 dark:text-white">
                          {course.name}
                        </h4>
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${subscriptionStyle.textColor} border ${subscriptionStyle.borderColor}`}>
                          {subscriptionStyle.icon}
                          {course.subscriptionType}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center gap-1">
                          <MdSchedule className="text-sm" />
                          <span>Enrolled: {new Date(course.enrolledDate).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MdAccessTime className="text-sm" />
                          <span>Self-paced learning</span>
                        </div>
                        {subscription?.expirationDate && (
                          <div className="flex items-center gap-1">
                            <span className="text-xs">
                              {isExpired ? 'Expired: ' : 'Expires: '}
                              {new Date(subscription.expirationDate).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Dynamic Status Badge */}
                    <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${statusStyle.bgColor} ${statusStyle.textColor} ${statusStyle.borderColor}`}>
                      {statusStyle.icon}
                      {statusStyle.text}
                    </div>
                  </div>

                  {/* Course Info Section */}
                  <div className="mb-4 p-3 bg-white/50 dark:bg-navy-900/30 rounded-lg border border-white/20 dark:border-navy-700/50">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${isExpired ? 'bg-orange-500' : 'bg-green-500'}`}></div>
                        <span className="text-gray-600 dark:text-gray-400">Status</span>
                        <span className="font-medium text-navy-700 dark:text-white">
                          {isExpired ? 'Expired' : 'Enrolled'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${isExpired ? 'bg-red-500' : 'bg-blue-500'}`}></div>
                        <span className="text-gray-600 dark:text-gray-400">Access</span>
                        <span className="font-medium text-navy-700 dark:text-white">
                          {isExpired ? 'Expired' : 'Unlimited'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {isExpired ? (
                        <button 
                          onClick={handleRenewSubscription}
                          className="flex items-center gap-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          <MdRefresh className="text-sm" />
                          Renew Subscription
                        </button>
                      ) : (
                        <button 
                          onClick={handleAccessCourse}
                          className="flex items-center gap-1 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          <MdPlayCircle className="text-sm" />
                          Access Course
                        </button>
                      )}
                      <button 
                        onClick={handleViewDetails}
                        className="flex items-center gap-1 px-3 py-2 bg-gray-100 dark:bg-navy-700 hover:bg-gray-200 dark:hover:bg-navy-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors"
                      >
                        <MdOpenInNew className="text-sm" />
                        View Details
                      </button>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button className="p-2 text-gray-400 hover:text-brand-500 transition-colors" title="Bookmark">
                        <MdBookmark className="text-sm" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-navy-700">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {tableData.length} course{tableData.length !== 1 ? 's' : ''} enrolled
          </div>
          <button 
            onClick={() => navigate('/admin/courses')}
            className="text-sm font-medium text-brand-500 hover:text-brand-600 transition-colors flex items-center gap-1"
          >
            Browse More Courses
            <MdOpenInNew className="text-xs" />
          </button>
        </div>
      </div>
    </Card>
  );
};

export default CourseTable;
